import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CakeOrders.css';

const statusFlow = [
  'placed',
  'accepted',
  'preparing',
  'decorating',
  'ready',
  'out_for_delivery',
  'completed',
];

const statusLabel = {
  placed: 'New Order',
  accepted: 'Accepted',
  preparing: 'Preparing',
  decorating: 'Decoration',
  ready: 'Ready',
  out_for_delivery: 'Out for Delivery',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const statusIcon = {
  placed: '🔔',
  accepted: '✓',
  preparing: '👨‍🍳',
  decorating: '🎨',
  ready: '🎂',
  out_for_delivery: '🛵',
  completed: '✅',
  cancelled: '✕',
};

const money = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN')}`;

const clean = (value) => String(value || '').trim();

const requirementRows = (customization = {}) => [
  ['Occasion', customization.occasion],
  ['Name on Cake', customization.recipient_name || customization.name_on_cake],
  ['Age / Number', customization.age || customization.age_number],
  ['Preferred Color', customization.preferred_color],
  ['Theme / Design', customization.theme],
  ['Shape', customization.shape],
  ['Cake Message', customization.cake_message],
  ['Special Request', customization.special_request],
];

const CakeOrders = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState('active');
  const [selected, setSelected] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');

  const token = () => localStorage.getItem('cakeToken') || '';

  const loadOrders = async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const auth = token();

      if (!auth) {
        navigate('/cake/login');
        return;
      }

      const response = await fetch('/api/cake/products/orders', {
        headers: {
          Authorization: `Bearer ${auth}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Orders load nahi hue');
      }

      const nextOrders = Array.isArray(data.orders) ? data.orders : [];
      setOrders(nextOrders);

      setSelected((current) => {
        if (!current) return null;

        const updated = nextOrders.find(
          (order) => Number(order.id) === Number(current.id)
        );

        return updated || current;
      });
    } catch (error) {
      setMessage(error.message || 'Orders load nahi hue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadOrders();

    const interval = window.setInterval(() => {
      if (!document.hidden) {
        loadOrders(false);
      }
    }, 20000);

    return () => {
      window.clearInterval(interval);
    };

  }, []);

  const activeOrders = useMemo(
    () =>
      orders.filter(
        (order) =>
          !['completed', 'cancelled'].includes(order.order_status)
      ),
    [orders]
  );

  const customOrders = useMemo(
    () =>
      activeOrders.filter((order) => order.order_type === 'custom'),
    [activeOrders]
  );

  const historyOrders = useMemo(
    () =>
      orders.filter((order) =>
        ['completed', 'cancelled'].includes(order.order_status)
      ),
    [orders]
  );

  const visibleOrders = useMemo(() => {
    if (active === 'custom') return customOrders;
    if (active === 'completed') return historyOrders;
    return activeOrders;
  }, [active, activeOrders, customOrders, historyOrders]);

  const updateStatus = async (order, nextStatus) => {
    try {
      setMessage('');

      const response = await fetch(
        `/api/cake/products/orders/${order.id}/status`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: nextStatus,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Status update failed');
      }

      await loadOrders(false);
    } catch (error) {
      setMessage(error.message || 'Status update failed');
    }
  };

  const getNextStatus = (order) => {
    if (!order || ['cancelled', 'completed'].includes(order.order_status)) {
      return null;
    }

    const index = statusFlow.indexOf(order.order_status);

    if (index < 0 || index >= statusFlow.length - 1) return null;

    if (
      order.fulfilment_type === 'pickup' &&
      order.order_status === 'ready'
    ) {
      return 'completed';
    }

    return statusFlow[index + 1];
  };

  const getStatusButtonText = (status) => {
    if (status === 'accepted') return 'Accept Order';
    if (status === 'preparing') return 'Start Preparing';
    if (status === 'decorating') return 'Start Decoration';
    if (status === 'ready') return 'Mark Ready';
    if (status === 'out_for_delivery') return 'Out for Delivery';
    if (status === 'completed') return 'Complete Order';

    return `Mark ${statusLabel[status] || status}`;
  };

  const orderItemsCount = (order) =>
    (order.items || []).reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );

  const hasImportantRequirements = (item) => {
    const c = item?.customization || {};

    return requirementRows(c).some(([, value]) => clean(value));
  };

  return (
    <div className="cake-orders-page">
      <aside className="cake-orders-sidebar">
        <div className="cake-orders-brand">
          <span>🎂</span>
          <div>
            <strong>VAKRA CAKE</strong>
            <small>Vendor Panel</small>
          </div>
        </div>

        <nav>
          <button onClick={() => navigate('/cake/vendor')}>
            🏠 Home
          </button>

          <button onClick={() => navigate('/cake/cakes')}>
            🛍️ Products
          </button>

          <button className="active">
            📦 Orders
          </button>

          <button onClick={() => navigate('/cake/settings')}>
            ⚙️ Settings
          </button>
        </nav>
      </aside>

      <main className="cake-orders-main">
        <header className="cake-orders-header">
          <div>
            <span>ORDER MANAGEMENT</span>
            <h1>Customer Orders</h1>
            <p>
              Normal aur custom cake orders ko complete requirements ke
              saath manage karein.
            </p>
          </div>

          <button
            type="button"
            disabled={refreshing}
            onClick={() => loadOrders(false)}
          >
            {refreshing ? 'Refreshing...' : '↻ Refresh'}
          </button>
        </header>

        <section className="cake-orders-stats">
          <div>
            <span>Active</span>
            <strong>{activeOrders.length}</strong>
          </div>

          <div className="custom-stat">
            <span>✨ Custom</span>
            <strong>{customOrders.length}</strong>
          </div>

          <div>
            <span>Completed / Cancelled</span>
            <strong>{historyOrders.length}</strong>
          </div>
        </section>

        {message && (
          <div className="cake-orders-message">
            {message}
          </div>
        )}

        <div className="cake-orders-tabs">
          <button
            className={active === 'active' ? 'active' : ''}
            onClick={() => setActive('active')}
          >
            Active Orders
          </button>

          <button
            className={active === 'custom' ? 'active' : ''}
            onClick={() => setActive('custom')}
          >
            ✨ Custom Cakes
          </button>

          <button
            className={active === 'completed' ? 'active' : ''}
            onClick={() => setActive('completed')}
          >
            History
          </button>
        </div>

        {loading ? (
          <div className="cake-orders-empty">
            Loading orders...
          </div>
        ) : visibleOrders.length === 0 ? (
          <div className="cake-orders-empty">
            <span>📦</span>
            <h3>No orders here</h3>
            <p>New customer orders yahan dikhai denge.</p>
          </div>
        ) : (
          <div className="cake-orders-list">
            {visibleOrders.map((order) => {
              const next = getNextStatus(order);
              const firstCustomItem = (order.items || []).find(
                (item) => item.customization
              );

              return (
                <article
                  key={order.id}
                  className={`cake-order-card ${
                    order.order_type === 'custom' ? 'custom' : ''
                  }`}
                >
                  <div className="cake-order-top">
                    <div>
                      <small>#{order.order_code}</small>
                      <h3>{order.customer_name}</h3>
                      <p>{order.customer_mobile}</p>
                    </div>

                    <div className="cake-order-badges">
                      {order.order_type === 'custom' && (
                        <span className="custom">
                          ✨ CUSTOM CAKE
                        </span>
                      )}

                      <span className={`status ${order.order_status}`}>
                        {statusIcon[order.order_status] || '•'}{' '}
                        {statusLabel[order.order_status] ||
                          order.order_status}
                      </span>
                    </div>
                  </div>

                  {order.order_type === 'custom' &&
                    firstCustomItem &&
                    hasImportantRequirements(firstCustomItem) && (
                      <div className="cake-order-priority-preview">
                        <strong>Customer Requirement</strong>

                        <span>
                          {[
                            firstCustomItem.customization?.occasion,
                            firstCustomItem.customization?.recipient_name,
                            firstCustomItem.customization?.cake_message,
                          ]
                            .filter(Boolean)
                            .join(' • ') || 'Open order to view details'}
                        </span>
                      </div>
                    )}

                  {order.reference_image_url && (
                    <div className="cake-order-photo-attached">
                      🖼️ Reference design attached
                    </div>
                  )}

                  <div className="cake-order-middle">
                    <div>
                      <small>Items</small>
                      <strong>{orderItemsCount(order)}</strong>
                    </div>

                    <div>
                      <small>
                        {order.fulfilment_type === 'pickup'
                          ? 'Pickup'
                          : 'Delivery'}
                      </small>

                      <strong>
                        {order.preferred_time_slot || 'Time not selected'}
                      </strong>
                    </div>

                    <div>
                      <small>Total</small>
                      <strong>{money(order.total_amount)}</strong>
                    </div>
                  </div>

                  <div className="cake-order-actions">
                    <button
                      className="secondary"
                      onClick={() => setSelected(order)}
                    >
                      View Full Requirements
                    </button>

                    {next && (
                      <button
                        onClick={() => updateStatus(order, next)}
                      >
                        {getStatusButtonText(next)}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {selected && (
        <div
          className="cake-order-modal-backdrop"
          onClick={() => setSelected(null)}
        >
          <section
            className="cake-order-modal cake-order-pro-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <header>
              <button onClick={() => setSelected(null)}>
                ←
              </button>

              <div>
                <strong>#{selected.order_code}</strong>
                <small>
                  {selected.order_type === 'custom'
                    ? 'Custom Cake Order'
                    : 'Cake Order'}
                </small>
              </div>

              <span className={`status-pill ${selected.order_status}`}>
                {statusLabel[selected.order_status]}
              </span>
            </header>

            <div className="cake-order-modal-body">
              {selected.order_type === 'custom' && (
                <div className="cake-order-important-banner">
                  <span>✨</span>
                  <div>
                    <strong>Custom Cake Requirements</strong>
                    <p>
                      Cake banane se pehle customer ki saari details aur
                      reference image check karein.
                    </p>
                  </div>
                </div>
              )}

              <section className="cake-order-section">
                <div className="cake-order-section-title">
                  <span>👤</span>
                  <div>
                    <h3>Customer Details</h3>
                    <small>Contact information</small>
                  </div>
                </div>

                <div className="cake-order-info-grid">
                  <div>
                    <small>Customer Name</small>
                    <strong>{selected.customer_name}</strong>
                  </div>

                  <div>
                    <small>Mobile Number</small>
                    <strong>{selected.customer_mobile}</strong>
                  </div>
                </div>
              </section>

              <section className="cake-order-section">
                <div className="cake-order-section-title">
                  <span>
                    {selected.fulfilment_type === 'pickup'
                      ? '🏪'
                      : '🛵'}
                  </span>

                  <div>
                    <h3>Delivery / Pickup Schedule</h3>
                    <small>Customer selected schedule</small>
                  </div>
                </div>

                <div className="cake-order-info-grid three">
                  <div>
                    <small>Order Mode</small>
                    <strong>
                      {selected.fulfilment_type === 'pickup'
                        ? 'Pickup'
                        : 'Delivery'}
                    </strong>
                  </div>

                  <div>
                    <small>Preferred Date</small>
                    <strong>
                      {selected.preferred_date || 'Not selected'}
                    </strong>
                  </div>

                  <div>
                    <small>Preferred Time</small>
                    <strong>
                      {selected.preferred_time_slot || 'Not selected'}
                    </strong>
                  </div>
                </div>

                {selected.delivery_address && (
                  <div className="cake-order-address-pro">
                    <small>Delivery Address</small>
                    <p>
                      {[
                        selected.delivery_address,
                        selected.landmark,
                        selected.pincode,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  </div>
                )}
              </section>

              <section className="cake-order-section">
                <div className="cake-order-section-title">
                  <span>🎂</span>
                  <div>
                    <h3>Order Items</h3>
                    <small>Products, size and flavor</small>
                  </div>
                </div>

                <div className="cake-order-detail-items pro">
                  {(selected.items || []).map((item) => {
                    const custom = item.customization || null;
                    const rows = custom
                      ? requirementRows(custom).filter(
                          ([, value]) => clean(value)
                        )
                      : [];

                    return (
                      <article
                        className={`cake-order-detail-item-pro ${
                          custom ? 'custom' : ''
                        }`}
                        key={item.id}
                      >
                        <div className="cake-order-detail-product-head">
                          <div>
                            {custom && (
                              <span className="cake-order-custom-tag">
                                ✨ CUSTOM
                              </span>
                            )}

                            <h4>
                              {item.product_name} × {item.quantity}
                            </h4>

                            <p>
                              {[item.variant, item.flavor]
                                .filter(Boolean)
                                .join(' • ') || 'Standard item'}
                            </p>
                          </div>

                          <strong>{money(item.line_total)}</strong>
                        </div>

                        {custom && (
                          <div className="cake-order-requirement-panel">
                            <div className="requirement-heading">
                              <strong>Customer Customization</strong>
                              <small>
                                Ye details cake preparation ke liye hain
                              </small>
                            </div>

                            <div className="cake-order-requirement-grid">
                              {rows.map(([label, value]) => (
                                <div
                                  key={label}
                                  className={
                                    ['Cake Message', 'Special Request', 'Theme / Design'].includes(
                                      label
                                    )
                                      ? 'wide important'
                                      : ''
                                  }
                                >
                                  <small>{label}</small>
                                  <strong>{value}</strong>
                                </div>
                              ))}
                            </div>

                            {(custom.requested_date ||
                              custom.requested_time ||
                              custom.fulfilment_type) && (
                              <div className="cake-order-item-schedule">
                                <span>🕒 Item Request:</span>
                                <strong>
                                  {[
                                    custom.fulfilment_type,
                                    custom.requested_date,
                                    custom.requested_time,
                                  ]
                                    .filter(Boolean)
                                    .join(' • ')}
                                </strong>
                              </div>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>

              {selected.reference_image_url && (
                <section className="cake-order-section reference-section">
                  <div className="cake-order-section-title">
                    <span>🖼️</span>
                    <div>
                      <h3>Customer Reference Design</h3>
                      <small>
                        Cake decoration ke liye uploaded image
                      </small>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="cake-order-reference-pro"
                    onClick={() =>
                      setPreviewImage(selected.reference_image_url)
                    }
                  >
                    <img
                      src={selected.reference_image_url}
                      alt="Customer cake reference"
                    />

                    <div>
                      <strong>Reference Image Attached</strong>
                      <small>Tap / click to view full image</small>
                    </div>

                    <span>View Full →</span>
                  </button>
                </section>
              )}

              {selected.customer_note && (
                <section className="cake-order-section customer-note-section">
                  <div className="cake-order-section-title">
                    <span>📝</span>
                    <div>
                      <h3>Customer Order Note</h3>
                      <small>Extra instruction from customer</small>
                    </div>
                  </div>

                  <div className="cake-order-customer-note-pro">
                    {selected.customer_note}
                  </div>
                </section>
              )}

              <section className="cake-order-section payment-section">
                <div className="cake-order-section-title">
                  <span>₹</span>
                  <div>
                    <h3>Payment Summary</h3>
                    <small>
                      {selected.payment_method === 'pay_at_shop'
                        ? 'Pay at shop'
                        : selected.payment_method === 'online'
                        ? 'Online payment'
                        : 'Cash on delivery'}
                    </small>
                  </div>
                </div>

                <div className="cake-order-payment-lines">
                  <div>
                    <span>Subtotal</span>
                    <strong>{money(selected.subtotal)}</strong>
                  </div>

                  <div>
                    <span>Delivery Charge</span>
                    <strong>{money(selected.delivery_charge)}</strong>
                  </div>

                  <div className="total">
                    <span>Total Amount</span>
                    <strong>{money(selected.total_amount)}</strong>
                  </div>
                </div>
              </section>

              <div className="cake-order-modal-actions pro-actions">
                {selected.order_status !== 'cancelled' &&
                  selected.order_status !== 'completed' && (
                    <button
                      className="danger"
                      onClick={() =>
                        updateStatus(selected, 'cancelled')
                      }
                    >
                      Cancel Order
                    </button>
                  )}

                {getNextStatus(selected) && (
                  <button
                    onClick={() =>
                      updateStatus(
                        selected,
                        getNextStatus(selected)
                      )
                    }
                  >
                    {getStatusButtonText(
                      getNextStatus(selected)
                    )}
                  </button>
                )}
              </div>
            </div>
          </section>
        </div>
      )}

      {previewImage && (
        <div
          className="cake-order-image-lightbox"
          onClick={() => setPreviewImage('')}
        >
          <button
            type="button"
            onClick={() => setPreviewImage('')}
          >
            ×
          </button>

          <img
            src={previewImage}
            alt="Customer reference full preview"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default CakeOrders;

